import { adaptArticlesEstimatedReadTime } from 'bundles/nostos-external/adapters';
import useNostosExternalData from 'bundles/nostos-external/useNostosExternalData';

type EstimatedReadTimeProps = {
  estimatedReadTime?: number;
  estimatedReadTimeLabel?: string;
  loading: boolean;
};

/**
 * Custom hook to fetch and return estimated read time data for an article (currently not supporting other SEO content page types).
 *
 * This hook queries the estimated read time and read time label for a given article slug
 * from the Nostos v3 external API (`articles_estimated_read_time`, registry table 120).
 * It returns the data along with the loading state.
 *
 * @param {{ slug: string }} params - Parameters for the query.
 * @param {string} params.slug - The slug of the article.
 * @returns {EstimatedReadTimeProps} The estimated read time data, label, and loading state.
 */
const useEstimatedReadTimeData = ({ slug }: { slug: string }): EstimatedReadTimeProps => {
  const { elements, loading, error } = useNostosExternalData({
    table: 'articles_estimated_read_time',
    keys: slug ? [slug] : [],
    adapt: adaptArticlesEstimatedReadTime,
    ssr: false,
  });

  if (error) {
    return {
      estimatedReadTime: undefined,
      estimatedReadTimeLabel: undefined,
      loading,
    };
  }

  const content = elements?.[0]?.content;

  return {
    estimatedReadTime: content?.estimatedreadingtime,
    estimatedReadTimeLabel: content?.estimatedreadingtimelabel,
    loading,
  };
};

export default useEstimatedReadTimeData;
